import { getTokens } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';

/**
 * Derives the deterministic mock TCAPI agency ID for a given authority.
 * This convention must match the seed script and debug panel so that
 * Shanghai can route callPresented events to the correct WebSocket.
 */
export function mockTcapiAgencyId(accountId: string): string {
  return `${accountId}-tcapi-agency`;
}

function authHeaders(): Record<string, string> {
  const token = getTokens()?.token;
  return {
    'Content-Type': 'application/json',
    // TCAPI credential endpoints require andromeda permissions, which are only
    // available on internal orgs. RapidSOS Admin is the internal org that all
    // devbox admin users belong to.
    'X-RapidSOS-Org': 'RapidSOS Admin',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function sendLocation({
  latitude,
  longitude,
  phoneNumber,
  source = 'CALL',
}: {
  latitude: number;
  longitude: number;
  phoneNumber: string;
  source?: string;
}) {
  const url = `${getAPIHost()}/v2/els/location`;
  const now = Date.now();

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    body: new URLSearchParams({
      v: '2',
      device_number: phoneNumber,
      time: `${now}`,
      location_time: `${now}`,
      location_latitude: `${latitude}`,
      location_longitude: `${longitude}`,
      location_accuracy: '2.5',
      source,
    }),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
}

/**
 * Triggers Shanghai's mock TCAPI server to push a callPresented event
 * to the text_to_911 service, creating a real database session.
 * The agencyId scopes the event to the WebSocket connection subscribed
 * for that agency — without it Shanghai cannot route the call.
 */
export async function simulateTcapiCall(
  phoneNumber: string,
  agencyId: string,
): Promise<{ callId: string; status: string }> {
  const url = `${getAPIHost()}/shanghai/tcapi_mock/simulate-call`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber, agencyId }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Shanghai mock TCAPI error (${response.status}): ${text}`);
  }
  return await response.json();
}

/**
 * Triggers Shanghai's mock TCAPI server to push a messageReceived event
 * to the text_to_911 service for an existing call.
 */
export async function simulateTcapiMessage(callId: string, message: string) {
  const url = `${getAPIHost()}/shanghai/tcapi_mock/simulate-message`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ callId, message }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Shanghai mock TCAPI error (${response.status}): ${text}`);
  }
  return await response.json();
}

/**
 * Triggers Shanghai's mock TCAPI server to push a ``callMissed`` event for
 * the most-recent presented (unaccepted) call to the given agency for the
 * given caller. On a soft-transfer conference leg this drives the real
 * backend timeout path (``handle_missed_soft_transfer`` →
 * ``TextSessionTransfer.status = timed_out``) — equivalent to InDigital's
 * "Conversation missed" after the receiver doesn't accept.
 *
 * Distinct from the frontend-only ``timed_out`` mock in MockTransferControls,
 * which only fakes a websocket message client-side and bypasses the backend.
 */
export async function simulateTcapiCallMissed(
  phoneNumber: string,
  agencyId: string,
): Promise<{ callId: string; status: string }> {
  const url = `${getAPIHost()}/shanghai/tcapi_mock/simulate-call-missed`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber, agencyId }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Shanghai mock TCAPI error (${response.status}): ${text}`);
  }
  return await response.json();
}

/**
 * Fetches existing TCAPI credentials for the given authority account.
 */
export async function getTcapiCredentials(
  accountId: string,
): Promise<{ id: number; tcapi_agency_id: string }[]> {
  const url = `${getAPIHost()}/v1/text_to_911/tcapi/credentials/${accountId}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      `Failed to fetch credentials (${response.status}): ${text}`,
    );
  }
  return await response.json();
}

/**
 * Seeds a mock TCAPI credential for the given authority account using a
 * deterministic agency ID derived from the accountId. If the credential
 * already exists, skips seeding. Never deletes existing credentials since
 * this code may run in deployed environments with real credentials.
 */
export async function seedTcapiCredential(accountId: string) {
  const agencyId = mockTcapiAgencyId(accountId);

  const existing = await getTcapiCredentials(accountId);
  const alreadySeeded = existing.some(c => c.tcapi_agency_id === agencyId);
  if (alreadySeeded) {
    // eslint-disable-next-line no-console -- debug panel: intentional user-facing diagnostic output
    console.warn(
      `[seedTcapiCredential] Credential for agency "${agencyId}" already exists, skipping.`,
    );
    return { skipped: true, agencyId };
  }

  const unexpected = existing.filter(c => c.tcapi_agency_id !== agencyId);
  if (unexpected.length > 0) {
    // eslint-disable-next-line no-console -- debug panel: intentional user-facing diagnostic output
    console.warn(
      `[seedTcapiCredential] Found ${unexpected.length} other credential(s) for account "${accountId}": ${unexpected.map(c => c.tcapi_agency_id).join(', ')}. Leaving them in place.`,
    );
  }

  const batchUrl = `${getAPIHost()}/v1/text_to_911/tcapi/credentials/${accountId}/batch`;
  const response = await fetch(batchUrl, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      add: [
        {
          agency_id: agencyId,
          secret_key: 'mock-secret-key',
          user_pass: 'mock-user:mock-password',
        },
      ],
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to seed credential (${response.status}): ${text}`);
  }
  return await response.json();
}
